import React from 'react';
import styled from 'styled-components';

import { GqlCustomNavigationItem } from 'types/graphql';

type BreadcrumbItem = {
  id: string | null;
  label: string;
};

type NavigationBreadcrumbsProps = {
  currentPath: string[];
  navigationItems: GqlCustomNavigationItem[];
  onNavigate: (path: string[]) => void;
  rootLabel?: string;
};

function buildBreadcrumbs(
  navigationItems: GqlCustomNavigationItem[],
  path: string[],
  rootLabel: string
): BreadcrumbItem[] {
  const initialState = {
    crumbs: [{ id: null, label: rootLabel }] as BreadcrumbItem[],
    currentItems: navigationItems,
  };

  const result = path.reduce((acc, sectionId) => {
    const section = acc.currentItems.find((item) => item.id === sectionId);
    if (section) {
      return {
        crumbs: [...acc.crumbs, { id: section.id, label: section.label }],
        currentItems: section.items,
      };
    }
    return acc;
  }, initialState);

  return result.crumbs;
}

export function NavigationBreadcrumbs({
  currentPath,
  navigationItems,
  onNavigate,
  rootLabel = 'Menu',
}: NavigationBreadcrumbsProps): JSX.Element | null {
  if (currentPath.length === 0) {
    return null;
  }

  const breadcrumbs = buildBreadcrumbs(navigationItems, currentPath, rootLabel);

  const handleCrumbClick = (index: number): void => {
    if (index === 0) {
      onNavigate([]);
    } else {
      onNavigate(currentPath.slice(0, index));
    }
  };

  return (
    <Container>
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        return (
          <CrumbWrapper key={crumb.id ?? 'root'}>
            <Crumb $isClickable={!isLast} onClick={() => !isLast && handleCrumbClick(index)}>
              {crumb.label}
            </Crumb>
            {!isLast && <Separator>/</Separator>}
          </CrumbWrapper>
        );
      })}
    </Container>
  );
}

const Container = styled.nav`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 16px;
`;

const CrumbWrapper = styled.span`
  align-items: center;
  display: flex;
  gap: 4px;
`;

const Crumb = styled.button<{ $isClickable: boolean }>`
  appearance: none;
  background: none;
  border: none;
  color: ${({ $isClickable }) => ($isClickable ? '#0b99e6' : '#4f5d68')};
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  font-size: 14px;
  font-weight: ${({ $isClickable }) => ($isClickable ? 'normal' : 'bold')};
  padding: 0;

  &:hover {
    text-decoration: ${({ $isClickable }) => ($isClickable ? 'underline' : 'none')};
  }
`;

const Separator = styled.span`
  color: #969ea5;
  font-size: 14px;
`;
